"use client"

import * as React from "react"
import {
  CaretSortIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@radix-ui/react-icons"
import * as SelectPrimitive from "@radix-ui/react-select"

import { cn } from "../../lib/utils"

const Select = SelectPrimitive.Root

const SelectGroup = SelectPrimitive.Group

const SelectValue = SelectPrimitive.Value

const DEFAULT_QA_PREFIX = "radix-select"

const qaSafe = (value: unknown) =>
  String(value ?? "unknown")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")

const getQaId = (
  fallback: string,
  props: Record<string, unknown>,
  suffix?: string | number
) => {
  const existingQa = props["data-qa"] || props["data-testid"] || props.id
  if (existingQa) return String(existingQa)

  if (suffix !== undefined && suffix !== null && String(suffix).trim() !== "") {
    return `${DEFAULT_QA_PREFIX}-${qaSafe(fallback)}-${qaSafe(suffix)}`
  }

  return `${DEFAULT_QA_PREFIX}-${qaSafe(fallback)}`
}

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, id, ...props }, ref) => {
  const qaId = getQaId("trigger", { ...props, id })

  return (
    <SelectPrimitive.Trigger
      ref={ref}
      id={id ?? qaId}
      data-testid={(props as any)["data-testid"] ?? qaId}
      data-qa={(props as any)["data-qa"] ?? qaId}
      className={cn(
        "flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
        className
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <CaretSortIcon
          className="h-4 w-4 opacity-50"
          data-testid={`${qaId}-icon`}
          data-qa={`${qaId}-icon`}
        />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  )
})
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, id, ...props }, ref) => {
  const qaId = getQaId("scroll-up-button", { ...props, id })

  return (
    <SelectPrimitive.ScrollUpButton
      ref={ref}
      id={id ?? qaId}
      data-testid={(props as any)["data-testid"] ?? qaId}
      data-qa={(props as any)["data-qa"] ?? qaId}
      className={cn(
        "flex cursor-default items-center justify-center py-1",
        className
      )}
      {...props}
    >
      <ChevronUpIcon
        data-testid={`${qaId}-icon`}
        data-qa={`${qaId}-icon`}
      />
    </SelectPrimitive.ScrollUpButton>
  )
})
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, id, ...props }, ref) => {
  const qaId = getQaId("scroll-down-button", { ...props, id })

  return (
    <SelectPrimitive.ScrollDownButton
      ref={ref}
      id={id ?? qaId}
      data-testid={(props as any)["data-testid"] ?? qaId}
      data-qa={(props as any)["data-qa"] ?? qaId}
      className={cn(
        "flex cursor-default items-center justify-center py-1",
        className
      )}
      {...props}
    >
      <ChevronDownIcon
        data-testid={`${qaId}-icon`}
        data-qa={`${qaId}-icon`}
      />
    </SelectPrimitive.ScrollDownButton>
  )
})
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = "popper", id, ...props }, ref) => {
  const qaId = getQaId("content", { ...props, id })

  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        id={id ?? qaId}
        data-testid={(props as any)["data-testid"] ?? qaId}
        data-qa={(props as any)["data-qa"] ?? qaId}
        className={cn(
          "relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          position === "popper" &&
            "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
          className
        )}
        position={position}
        {...props}
      >
        <SelectScrollUpButton data-qa={`${qaId}-scroll-up-button`} />
        <SelectPrimitive.Viewport
          data-testid={`${qaId}-viewport`}
          data-qa={`${qaId}-viewport`}
          className={cn(
            "p-1",
            position === "popper" &&
              "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]"
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton data-qa={`${qaId}-scroll-down-button`} />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  )
})
SelectContent.displayName = SelectPrimitive.Content.displayName

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, id, ...props }, ref) => {
  const qaId = getQaId("label", { ...props, id })

  return (
    <SelectPrimitive.Label
      ref={ref}
      id={id ?? qaId}
      data-testid={(props as any)["data-testid"] ?? qaId}
      data-qa={(props as any)["data-qa"] ?? qaId}
      className={cn("px-2 py-1.5 text-sm font-semibold", className)}
      {...props}
    />
  )
})
SelectLabel.displayName = SelectPrimitive.Label.displayName

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, id, ...props }, ref) => {
  const qaId = getQaId("item", { ...props, id }, (props as any).value)

  return (
    <SelectPrimitive.Item
      ref={ref}
      id={id ?? qaId}
      data-testid={(props as any)["data-testid"] ?? qaId}
      data-qa={(props as any)["data-qa"] ?? qaId}
      data-option-value={(props as any).value}
      className={cn(
        "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        className
      )}
      {...props}
    >
      <span
        className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center"
        data-testid={`${qaId}-indicator-wrapper`}
        data-qa={`${qaId}-indicator-wrapper`}
      >
        <SelectPrimitive.ItemIndicator>
          <CheckIcon
            className="h-4 w-4"
            data-testid={`${qaId}-check-icon`}
            data-qa={`${qaId}-check-icon`}
          />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  )
})
SelectItem.displayName = SelectPrimitive.Item.displayName

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, id, ...props }, ref) => {
  const qaId = getQaId("separator", { ...props, id })

  return (
    <SelectPrimitive.Separator
      ref={ref}
      id={id ?? qaId}
      data-testid={(props as any)["data-testid"] ?? qaId}
      data-qa={(props as any)["data-qa"] ?? qaId}
      className={cn("-mx-1 my-1 h-px bg-muted", className)}
      {...props}
    />
  )
})
SelectSeparator.displayName = SelectPrimitive.Separator.displayName

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
}
